import { Directive, OnInit, OnDestroy, Input, Output, ElementRef, Renderer2, EventEmitter, SimpleChanges } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { takeUntilDestroyed } from 'src/app/utils/take-until-destroyed';
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, tap, switchMap, takeUntil, catchError } from "rxjs/operators";
import { MemberService } from "src/app/component/members/member-service/member.service";


@Directive({
  selector: '[appMemberAutocomplete]'
})
export class MemberAutocompleteDirective implements OnInit, OnDestroy {
  @Input() skip = 0;
  @Input() limit = 10;
  @Input() minLength = 3;
  @Input() debounceTimeValue = 300;
  @Input() additionalKeys = {};
  @Input() scrollContainer: HTMLElement | null = null;
  @Input() showDropdown = true;

  @Output() memberSelected = new EventEmitter<any>();
  @Output() memberListUpdated = new EventEmitter<any[]>();

  members: any[] = [];
  queryControl: UntypedFormControl = new UntypedFormControl('');
  hasMore = true;
  loading = false;
  private searchSubscription: Subscription | undefined;
  private dropdownElement: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private memberService: MemberService,
    private renderer: Renderer2
  ) { }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['additionalKeys'] && !changes['additionalKeys'].firstChange) {
      this.loadMembers()
    }
}

  ngOnInit() {
    const inputElement = this.el.nativeElement;

    this.queryControl.valueChanges.pipe(
      debounceTime(this.debounceTimeValue),
      distinctUntilChanged(),
      filter(query => query.trim().length >= this.minLength || query.trim().length === 0),
      tap(() => {
        this.members = [];
        this.skip = 0;
        this.hasMore = true;
        if (this.showDropdown) this.createDropdown();
      }),
      switchMap(query => {
        if (query.trim().length === 0) {
          this.members = [];
          this.closeDropdown();
          return [];
        }
        return this.memberService.getMembers({...this.additionalKeys, displayName: query, limit: this.limit, skip: this.skip }).pipe(
          takeUntil(takeUntilDestroyed(this))
        );
      }),
      catchError(err => {
        console.error('Error occurred:', err);
        this.loading = false;
        return [];
      })
    ).subscribe(data => {
      this.members.push(...data);
      this.hasMore = data.length > 0;
      this.skip += data.length;
      this.loading = false;
      if (this.showDropdown) this.updateDropdown();

      // Emit the updated list of members
      this.memberListUpdated.emit(this.members);
    });

    inputElement.addEventListener('input', () => {
      this.queryControl.setValue(inputElement.value);
    });

    inputElement.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Backspace') {
        if (inputElement.value === '') {
          this.members = [];
          this.hasMore = true;
          this.skip = 0;
          this.closeDropdown();
          this.memberSelected.emit(null);
          this.memberListUpdated.emit(this.members);  // Emit the empty list
          return;
        }
        this.memberSelected.emit(null);
      }
    });

    document.addEventListener('click', (event: any) => {
      if (
        !this.dropdownElement ||
        !this.dropdownElement.contains(event.target) &&
        event.target !== inputElement
      ) {
        this.closeDropdown();
      }
    });

    if (this.scrollContainer) {
      this.renderer.listen(this.scrollContainer, 'scroll', (event: any) => {
        if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight - 10) {
          this.loadMore();
        }
      });
    }

    this.loadMembers();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.closeDropdown();
  }

  loadMore() {
    if (!this.hasMore || this.loading) return;

    this.loading = true;
    this.memberService.getMembers({ ...this.additionalKeys, displayName: this.queryControl.value, limit: this.limit, skip: this.skip }).subscribe(data => {
      this.members.push(...data);
      this.hasMore = data.length > 0;
      this.skip += data.length;
      this.loading = false;
      if (this.showDropdown) this.updateDropdown();

      // Emit the updated list of members
      this.memberListUpdated.emit(this.members);
    });
  }

  loadMembers() {

    this.members = [];
    this.skip = 0;
    this.hasMore = true;
    this.loading = true;
    if (this.scrollContainer) {
    this.scrollContainer.scrollTop = 0;
    }
    this.memberService.getMembers({
      ...this.additionalKeys,
       ...(this.queryControl.value && this.queryControl.value.length ? { displayName: this.queryControl.value } : {}),
      limit: this.limit, skip: this.skip
    }).subscribe(data => {
      this.members.push(...data);
      this.hasMore = data.length > 0;
      this.skip += data.length;
      this.loading = false;
      if (this.showDropdown) this.updateDropdown();
      this.memberListUpdated.emit(this.members);
    });
  }

  selectMember(member: any) {
    this.memberSelected.emit(member);
    this.closeDropdown();
  }

  private updateDropdown() {
    if (!this.dropdownElement) return;

    this.dropdownElement.innerHTML = '';

    const ul = this.renderer.createElement('ul');
    this.renderer.addClass(ul, 'autocomplete-list');

    this.members.forEach((member) => {
      const li = this.renderer.createElement('li');
      this.renderer.addClass(li, 'autocomplete-item');
      this.renderer.setStyle(li, 'padding', '8px 16px');
      this.renderer.setStyle(li, 'cursor', 'pointer');
      this.renderer.setStyle(li, 'border-bottom', '1px solid #f0f0f0');
      this.renderer.setStyle(li, 'font-size', '14px');

      this.renderer.listen(li, 'mouseenter', () => {
        this.renderer.setStyle(li, 'background-color', '#5063B6');
        this.renderer.setStyle(li, 'color', 'white');
      });

      this.renderer.listen(li, 'mouseleave', () => {
        this.renderer.setStyle(li, 'background-color', 'transparent');
        this.renderer.setStyle(li, 'color', 'black');
      });

      const text = this.renderer.createText(`${this.capitalizeWords(member.displayName)}${member.contactNumber ? (' - ' + member.contactNumber) : ''}`);
      this.renderer.appendChild(li, text);

      this.renderer.listen(li, 'click', () => this.selectMember(member));

      this.renderer.appendChild(ul, li);
    });

    this.renderer.appendChild(this.dropdownElement, ul);

    const rect = this.el.nativeElement.getBoundingClientRect();
    this.renderer.setStyle(this.dropdownElement, 'top', `${rect.bottom + window.scrollY}px`);
    this.renderer.setStyle(this.dropdownElement, 'left', `${rect.left + window.scrollX}px`);
    this.renderer.setStyle(this.dropdownElement, 'width', `${rect.width}px`);

    this.renderer.setStyle(this.dropdownElement, 'display', 'block');
  }

  private createDropdown() {
    if (!this.dropdownElement && this.showDropdown) {
      this.dropdownElement = this.renderer.createElement('div');
      this.renderer.addClass(this.dropdownElement, 'autocomplete-dropdown');
      this.renderer.setStyle(this.dropdownElement, 'position', 'absolute');
      this.renderer.setStyle(this.dropdownElement, 'z-index', '1000');
      this.renderer.setStyle(this.dropdownElement, 'max-height', '300px');
      this.renderer.setStyle(this.dropdownElement, 'overflow-y', 'auto');
      this.renderer.setStyle(this.dropdownElement, 'background-color', 'white');
      this.renderer.setStyle(this.dropdownElement, 'box-shadow', '0 4px 6px rgba(0, 0, 0, 0.1)');
      this.renderer.setStyle(this.dropdownElement, 'border', '1px solid #ddd');
      this.renderer.setStyle(this.dropdownElement, 'border-radius', '8px');
      this.renderer.setStyle(this.dropdownElement, 'padding', '5px 0');
      this.renderer.appendChild(document.body, this.dropdownElement);

      this.renderer.listen(this.dropdownElement, 'scroll', (event: any) => {
        if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight - 10) {
          this.loadMore();
        }
      });
    }
  }

  private closeDropdown() {
    if (this.dropdownElement) {
      this.renderer.removeChild(document.body, this.dropdownElement);
      this.dropdownElement = null;
    }
  }

  private capitalizeWords(str) {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
}





